/**
 * NewVendorForm
 * The "Add a new vendor" step of the email link dialog (BR-126): creates a vendor and links the
 * thread in one action. Name is required, Trades reuses the vendor form's trade options, and
 * Email is prefilled with the unrecognized sender so the new vendor's contact email is the
 * address that just wrote in.
 */

import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { TradesCheckboxGroup } from '../vendors/TradesCheckboxGroup';

export interface NewVendorValues {
  name: string;
  trades: string[];
  email: string;
}

interface NewVendorFormProps {
  senderAddress: string;
  submitting: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (values: NewVendorValues) => void;
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground min-h-[44px]';

export function NewVendorForm({
  senderAddress,
  submitting,
  onBack,
  onCancel,
  onSubmit,
}: NewVendorFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [trades, setTrades] = useState<string[]>([]);
  const [email, setEmail] = useState(senderAddress);

  return (
    <form
      data-testid="new-vendor-form"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit({ name: name.trim(), trades, email: email.trim() });
      }}
    >
      <div>
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={submitting}
          data-testid="new-vendor-back"
          className="-ml-2 px-2"
        >
          <ChevronLeft aria-hidden="true" />
          Choose an existing vendor
        </Button>
      </div>

      <div>
        <label htmlFor="new-vendor-name" className="block text-sm font-medium text-foreground mb-1.5">
          Name <span className="font-normal text-muted-foreground">(required)</span>
        </label>
        <input
          id="new-vendor-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          aria-required="true"
          data-testid="new-vendor-name"
          className={inputClass}
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-foreground mb-1.5">Trades</span>
        <TradesCheckboxGroup selected={trades} onChange={setTrades} testIdPrefix="new-vendor-trade" />
      </div>

      <div>
        <label htmlFor="new-vendor-email" className="block text-sm font-medium text-foreground mb-1.5">
          Email
        </label>
        <input
          id="new-vendor-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-describedby="new-vendor-email-help"
          data-testid="new-vendor-email"
          className={inputClass}
        />
        <p id="new-vendor-email-help" className="mt-1.5 text-xs text-muted-foreground">
          This becomes the vendor&apos;s contact email. We write to it first and recognize mail from it. You
          can add more addresses later.
        </p>
      </div>

      <div className="flex gap-3 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          data-testid="new-vendor-cancel"
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!name.trim() || submitting} data-testid="new-vendor-submit">
          {submitting ? 'Creating...' : 'Create vendor and link'}
        </Button>
      </div>
    </form>
  );
}
